import * as signalR from '@microsoft/signalr';
import { supabase } from '../supabase/client';

// Debug flag
const DEBUG = true;

/**
 * Helper method to handle client-side event emission (for testing and local handling)
 * @param {Object} connection - The SignalR connection object
 * @param {string} eventName - The name of the event to emit
 * @param {any} data - The data to pass with the event
 * @returns {boolean} Whether the event was emitted successfully
 */
const emitClientEvent = (connection, eventName, data) => {
  if (!connection) return false;

  try {
    // Check if we have listeners for this event
    const listeners = connection._methods && connection._methods[eventName];
    
    if (listeners && listeners.length > 0) {
      // Call each listener with the data
      listeners.forEach(listener => {
        try {
          listener(data);
        } catch (err) {
          console.error(`Error in ${eventName} listener:`, err);
        }
      });
      return true;
    }
    return false;
  } catch (err) {
    console.error(`Error emitting ${eventName} event:`, err);
    return false;
  }
};

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.messageHandlers = [];
    this.sessionMessagesHandlers = [];
    this.historicMessagesHandlers = [];
    this.sessionIdHandlers = [];
    this.connectionPromise = null;
    
    // Add this.emit method for easier access to emitClientEvent
    this.emit = this.emitClientEvent.bind(this);
  }

  /**
   * Get the latest JWT access token from Supabase
   * @returns {Promise<string>} The access token
   */
  async getAccessToken() {
    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token;
    
    if (!accessToken) {
      throw new Error('No access token available');
    }
    
    return accessToken;
  }

  /**
   * Add emit method to the connection if it doesn't exist
   * @private
   */
  _addEmitSupport() {
    if (!this.connection) return;
    
    if (!this.connection.emit) {
      this.connection.emit = (eventName, data) => {
        return emitClientEvent(this.connection, eventName, data);
      };
    }
  }

  /**
   * Initialize the SignalR connection
   * @returns {Promise} Promise that resolves when connection is established
   */
  async initializeConnection() {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise(async (resolve, reject) => {
      try {
        // Get the auth token
        const accessToken = await this.getAccessToken();
        
        // Create the connection with proper URL normalization
        let baseUrl = process.env.REACT_APP_POKKETAPI_URL || 'https://dev1.pokketcoach.com:1338';
        
        // Remove trailing slash if it exists
        if (baseUrl.endsWith('/')) {
          baseUrl = baseUrl.slice(0, -1);
        }
        
        // Hub path with leading slash
        const hubPath = '/pokket-live/chat/';
        const hubUrl = `${baseUrl}${hubPath}`;
        
        if (DEBUG) {
          console.log('Initializing SignalR connection to:', hubUrl);
          console.log('Using JWT token (first 10 chars):', accessToken.substring(0, 10) + '...');
        }

        // Create SignalR connection
        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            // Always get a fresh token when connecting or reconnecting
            accessTokenFactory: async () => {
              try {
                return await this.getAccessToken();
              } catch (error) {
                console.error('Failed to get access token:', error);
                throw error;
              }
            },
            transport: signalR.HttpTransportType.WebSockets,
            skipNegotiation: true
          })
          .withAutomaticReconnect([0, 2000, 10000, 30000]) // Retry quickly at first, then with increasing delays
          .configureLogging(signalR.LogLevel.Information)
          .build();
          
        // Add emit support
        this._addEmitSupport();

        // Set up message handler
        this.connection.on('ReceiveMessage', (message) => {
          if (DEBUG) {
            console.log('SignalR received message:', message);
            
            // Add detailed message structure debugging
            if (message) {
              console.log('Message keys:', Object.keys(message));
              
              // Check for suggestedResponses in various possible locations
              if (message.suggestedResponses) {
                console.log('Direct suggestedResponses found:', message.suggestedResponses);
              } else if (message.message && message.message.suggestedResponses) {
                console.log('Nested suggestedResponses found in message.message:', message.message.suggestedResponses);
              } else if (message.data && message.data.suggestedResponses) {
                console.log('Nested suggestedResponses found in message.data:', message.data.suggestedResponses);
              } else {
                console.log('No suggestedResponses found in message');
              }
            }
          }
          
          // Notify all registered handlers
          this.messageHandlers.forEach(handler => handler(message));
        });

        // Set up session messages handler for historical messages
        this.connection.on('ReceiveSessionMessages', (messages) => {
          if (DEBUG) {
            console.log('SignalR received session messages:', messages);
          }
          
          // Notify all registered session message handlers
          this.sessionMessagesHandlers.forEach(handler => handler(messages));
        });
        
        // Set up handler for historic messages (older messages from previous sessions)
        this.connection.on('ReceiveHistoricMessages', (messages) => {
          if (DEBUG) {
            console.log('SignalR received historic messages:', messages);
          }
          
          // Notify all registered historic message handlers
          this.historicMessagesHandlers.forEach(handler => handler(messages));
        });
        
        // Set up handler for receiving session ID from server
        this.connection.on('IssueSessionID', (sessionId) => {
          if (DEBUG) {
            console.log('SignalR received session ID:', sessionId);
          }
          
          // Store session ID in localStorage for persistence
          if (sessionId && typeof sessionId === 'string') {
            localStorage.setItem('pokket_chat_session_id', sessionId);
          }
          
          // Notify all registered session ID handlers
          this.sessionIdHandlers.forEach(handler => handler(sessionId));
        });

        // Add explicit handler for SuggestedQuickResponses if it's a separate event
        this.connection.on('SuggestedQuickResponses', (suggestions) => {
          if (DEBUG) {
            console.log('SignalR received SuggestedQuickResponses event:', suggestions);
          }
          
          // Construct a message-like object to reuse existing handler mechanism
          const suggestionsMessage = {
            suggestedResponses: Array.isArray(suggestions) ? suggestions : [suggestions],
            isFromUser: false,
            isSuggestionEvent: true
          };
          
          // Notify message handlers of this suggestion event
          this.messageHandlers.forEach(handler => handler(suggestionsMessage));
        });

        // Handle reconnection events
        this.connection.onreconnecting((error) => {
          console.warn('SignalR reconnecting due to error:', error);
          this.isConnected = false;
        });
        
        this.connection.onreconnected((connectionId) => {
          console.log('SignalR reconnected with ID:', connectionId);
          this.isConnected = true;
        });
        
        this.connection.onclose((error) => {
          console.warn('SignalR connection closed:', error);
          this.isConnected = false;
          this.connectionPromise = null;
        });

        // Start the connection
        await this.connection.start();
        this.isConnected = true;
        
        if (DEBUG) {
          console.log('SignalR connection established');
          
          // Debug: Try to get available methods on the hub
          this.logAvailableMethods();
        }
        
        resolve();
      } catch (error) {
        console.error('Error establishing SignalR connection:', error);
        
        // Check if this is a 402 error (subscription required)
        // Look for 402 status code or payment/subscription mentions in the error
        const isSubscriptionError = 
          (error.statusCode === 402) || 
          (error.message && (
            error.message.includes('402') || 
            error.message.includes('Payment Required') || 
            error.message.includes('subscription') || 
            error.message.includes('subscribe')
          ));
        
        if (isSubscriptionError) {
          console.warn('SignalR connection failed due to subscription requirement');
          // Create a custom error with subscription info
          const subscriptionError = new Error('Subscription required to connect to chat');
          subscriptionError.isSubscriptionError = true;
          subscriptionError.originalError = error;
          this.connectionPromise = null;
          reject(subscriptionError);
        } else {
          this.connectionPromise = null;
          reject(error);
        }
      }
    });

    return this.connectionPromise;
  }

  /**
   * Debug utility: Try to log available methods on the hub
   * This is an attempt to discover what methods are available
   */
  async logAvailableMethods() {
    if (!this.isConnected || !this.connection) {
      console.warn('Cannot log available methods - not connected');
      return;
    }
    
    try {
      // Try to invoke a method that might return available methods
      // Note: This is speculative and may not work on all SignalR hubs
      console.log('Attempting to discover SignalR hub methods...');
      
      // Log connection object properties
      console.log('Connection methods:', Object.getOwnPropertyNames(this.connection).filter(
        prop => typeof this.connection[prop] === 'function'
      ));
      
      // Try to invoke a built-in method or inspect connection
      try {
        const serverMethods = await this.connection.invoke('GetServerMethods');
        console.log('Available server methods:', serverMethods);
      } catch (e) {
        console.log('GetServerMethods not available (expected)');
      }
      
      // Try to invoke a method that might list available methods
      try {
        console.log('Trying to get available hub methods...');
        const availableMethods = await this.connection.invoke('GetAvailableMethods');
        console.log('Hub methods:', availableMethods);
      } catch (e) {
        console.log('GetAvailableMethods not available');
      }
      
      // Log registered client methods (events we listen to)
      if (this.connection._methods) {
        console.log('Registered client methods:', Object.keys(this.connection._methods));
      }
      
      // Debug: log functions on connection object
      const connectionFunctions = [];
      for (const key in this.connection) {
        if (typeof this.connection[key] === 'function') {
          connectionFunctions.push(key);
        }
      }
      console.log('Connection functions:', connectionFunctions);
    } catch (error) {
      console.warn('Method discovery failed (expected):', error);
    }
  }

  /**
   * Emit a client-side event (for testing or local event handling)
   * @param {string} eventName - The name of the event to emit
   * @param {any} data - The data to pass with the event
   * @returns {boolean} - Whether the event was emitted successfully
   */
  emitClientEvent(eventName, data) {
    return emitClientEvent(this.connection, eventName, data);
  }

  /**
   * Send a message via SignalR
   * @param {string} sessionId - The chat session ID
   * @param {object} message - The message to send
   * @returns {Promise} Promise that resolves when the message is sent
   */
  async sendMessage(sessionId, message) {
    try {
      if (!this.isConnected) {
        await this.initializeConnection();
      }

      const formattedMessage = {
        message: message.message || message,
        timestamp: new Date().toISOString(),
        metadata: message.metadata || {
          inputType: "text"
        }
      };

      if (DEBUG) {
        console.log('Sending message through SignalR:', {
          sessionId,
          message: formattedMessage
        });
      }

      await this.connection.invoke('SendMessage', sessionId, formattedMessage);
      return true;
    } catch (error) {
      console.error('Error sending message via SignalR:', error);
      throw error;
    }
  }

  /**
   * Register a handler for messages received from SignalR
   * @param {Function} handler - Function to call when a message is received
   */
  onMessage(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Message handler must be a function');
    }
    
    this.messageHandlers.push(handler);
    
    // Return a function to remove this handler
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for session messages received from SignalR
   * @param {Function} handler - Function to call when session messages are received
   */
  onSessionMessages(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Session messages handler must be a function');
    }
    
    this.sessionMessagesHandlers.push(handler);
    
    // Return a function to remove this handler
    return () => {
      this.sessionMessagesHandlers = this.sessionMessagesHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Register a handler for historic messages received from SignalR
   * @param {Function} handler - Function to call when historic messages are received
   */
  onHistoricMessages(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Historic messages handler must be a function');
    }
    
    this.historicMessagesHandlers.push(handler);
    
    // Return a function to remove this handler
    return () => {
      this.historicMessagesHandlers = this.historicMessagesHandlers.filter(h => h !== handler);
    };
  }
  
  /**
   * Register a handler for session ID received from SignalR
   * @param {Function} handler - Function to call when a session ID is received
   */
  onSessionId(handler) {
    if (typeof handler !== 'function') {
      throw new Error('Session ID handler must be a function');
    }
    
    this.sessionIdHandlers.push(handler);
    
    // Return a function to remove this handler
    return () => {
      this.sessionIdHandlers = this.sessionIdHandlers.filter(h => h !== handler);
    };
  }

  /**
   * Get historic messages for a session
   * @param {string} sessionId - The session ID
   * @param {number} offset - Offset from the latest messages (0 for newest)
   * @param {number} count - Number of messages to fetch
   * @returns {Promise} Promise that resolves when the request is sent
   */
  async getHistoricMessages(sessionId, offset, count = 20) {
    try {
      if (!this.isConnected) {
        await this.initializeConnection();
      }

      if (DEBUG) {
        console.log('Requesting historic messages:', {
          sessionId,
          offset,
          count
        });
      }

      await this.connection.invoke('GetHistoricMessages', sessionId, offset, count);
      return true;
    } catch (error) {
      console.error('Error getting historic messages via SignalR:', error);
      throw error;
    }
  }

  /**
   * Get the current connection state
   * @returns {Object} The current connection state information
   */
  getConnectionState() {
    return {
      isConnected: this.isConnected,
      connectionState: this.connection ? this.connection.state : "Disconnected",
      connectionId: this.connection ? this.connection.connectionId : null
    };
  }

  /**
   * Start a new connection or return the existing one
   * @returns {Promise} Promise that resolves when the connection is established
   */
  async ensureConnection() {
    if (this.isConnected && this.connection) {
      return this.connection;
    }
    
    await this.initializeConnection();
    return this.connection;
  }

  /**
   * Reset the service completely - clearing all handlers, state and stored data
   * To be used during logout to prevent data leakage between users
   */
  async resetService() {
    try {
      if (DEBUG) {
        console.log('Resetting SignalR service completely');
      }
      
      // Disconnect if connected
      if (this.connection) {
        try {
          await this.connection.stop();
          console.log('SignalR connection closed during reset');
        } catch (error) {
          console.warn('Error stopping SignalR connection during reset:', error);
        }
      }
      
      // Clear all handlers
      this.messageHandlers = [];
      this.sessionMessagesHandlers = [];
      this.historicMessagesHandlers = [];
      this.sessionIdHandlers = [];
      
      // Reset connection state
      this.connection = null;
      this.isConnected = false;
      this.connectionPromise = null;
      
      // Clear ALL pokket-related data from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('pokket_')) {
          localStorage.removeItem(key);
        }
      });
      
      // Also clear session-related items specifically
      localStorage.removeItem('pokket_chat_session_id');
      localStorage.removeItem('pokket_chat_session_timestamp');
      localStorage.removeItem('viewedSessions');
      localStorage.removeItem('interactedSessions');
      
      return true;
    } catch (error) {
      console.error('Error resetting SignalR service:', error);
      return false;
    }
  }

  /**
   * Disconnect from SignalR
   */
  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('SignalR connection closed');
        this.isConnected = false;
        this.connectionPromise = null;
      } catch (error) {
        console.error('Error disconnecting from SignalR:', error);
      }
    }
  }
}

// Create and export a singleton instance
const signalRService = new SignalRService();
export default signalRService; 